import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Save, Loader2, ChevronLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import DocumentHeader from '../components/DocumentHeader';
import VoiceTextInput from '../components/VoiceTextInput';
import Genogram from '../components/Genogram';
import CycleOfAddictionWorksheet from '../components/CycleOfAddictionWorksheet';
import CycleOfAddictionDiagramW3 from '../components/CycleOfAddictionDiagramW3';
import { weekLabel, bareWeekLabel } from '../lib/weekLabels';
import useAllowPrint from '../components/useAllowPrint';

const AUTOSAVE_MS = 5000;
// Week 3 SW spec — a field is "completed" only at >=20 chars (§5 + §6).
const W3_THRESHOLD = 20;

/**
 * SelfWorkWorkbookPage  --  one workbook page (one (kind, week) pair).
 * Auto-saves every question every 5 seconds when its content changes.
 */
export default function SelfWorkWorkbookPage() {
  useAllowPrint();
  const { kind, week } = useParams();
  const weekNum = parseInt(week, 10);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [completed, setCompleted] = useState(false);
  const [allRequiredAnswered, setAllRequiredAnswered] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showW3PartialConfirm, setShowW3PartialConfirm] = useState(false);
  const dirtyRef = useRef({});            // questionId -> true when text changed but not yet saved
  const lastSentRef = useRef({});         // questionId -> last value sent (to avoid no-op writes)

  // Week 3 detection — drives the bespoke render path, the 20-char threshold,
  // and the partial-submit confirmation modal.
  // Programme Week 3 lives at internal slot 5 (after the +2 offset for the
  // 2 Week Window).  This is the slot that gets the bespoke Cycle of
  // Addiction SVG + 12-field workbook.
  const CYCLE_OF_ADDICTION_INTERNAL = 5;
  const isW3 = kind === 'programme' && weekNum === CYCLE_OF_ADDICTION_INTERNAL;

  useEffect(() => {
    fetchWorkbook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, week]);

  const fetchWorkbook = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/self-work/${kind}/${weekNum}`);
      if (r.ok) {
        const d = await r.json();
        setQuestions(d.questions || []);
        setAnswers(d.drafts || {});
        setCompleted(!!d.completed);
        setAllRequiredAnswered(!!d.all_required_answered);
        lastSentRef.current = { ...(d.drafts || {}) };
      } else {
        toast.error('Could not load this self-work.');
      }
    } catch {
      toast.error('Network error.  Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---- auto-save loop  --  one debounced save per question every 5s ----
  const saveOne = useCallback(async (qid, val) => {
    if (lastSentRef.current[qid] === val) return;
    setSaving(s => ({ ...s, [qid]: true }));
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/self-work/${kind}/${weekNum}/draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: qid, answer: val }),
      });
      if (r.ok) {
        lastSentRef.current[qid] = val;
        setLastSavedAt(new Date());
        dirtyRef.current[qid] = false;
      }
    } catch (e) {
      console.warn('autosave failed for', qid, e);
    } finally {
      setSaving(s => ({ ...s, [qid]: false }));
    }
  }, [kind, weekNum]);

  // Tick every AUTOSAVE_MS, save every dirty question
  useEffect(() => {
    const id = setInterval(() => {
      Object.keys(dirtyRef.current).forEach(qid => {
        if (dirtyRef.current[qid]) saveOne(qid, answers[qid] ?? '');
      });
    }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [answers, saveOne]);

  // Save on tab close / route leave
  useEffect(() => {
    const handler = () => {
      Object.keys(dirtyRef.current).forEach(qid => {
        if (dirtyRef.current[qid]) {
          // Best-effort sync save  --  fire and forget;  service workers don't block the unload path.
          navigator.sendBeacon?.(
            `${process.env.REACT_APP_BACKEND_URL}/api/self-work/${kind}/${weekNum}/draft`,
            new Blob([JSON.stringify({ question_id: qid, answer: answers[qid] ?? '' })], { type: 'application/json' })
          );
        }
      });
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [answers, kind, weekNum]);

  const updateAnswer = (qid, val) => {
    setAnswers(prev => ({ ...prev, [qid]: val }));
    dirtyRef.current[qid] = true;
  };

  // Save immediately on blur (Week 3 spec §5).  Falls back to the 5s tick
  // for other weeks via the dirtyRef loop above.
  const saveOnBlur = (qid) => {
    if (dirtyRef.current[qid]) saveOne(qid, answers[qid] ?? '');
  };

  // Week 3 progress: a field counts when its trimmed length meets the 20-char
  // threshold (§5 + §6).  Required ids are the 8 stage_* + 4 ref_* fields.
  const w3RequiredIds = isW3
    ? questions.filter(q => q.type === 'longtext_w3_stage' || q.type === 'longtext_w3_reflection').map(q => q.id)
    : [];
  const w3CountValid = isW3
    ? w3RequiredIds.filter(id => (answers[id] || '').trim().length >= W3_THRESHOLD).length
    : 0;
  const w3Total = isW3 ? w3RequiredIds.length : 0;          // 12
  const w3Percent = w3Total ? Math.round((w3CountValid / w3Total) * 100) : 0;
  const w3MeetsHardGate = isW3 && w3CountValid >= 8;        // §7 partial-submit threshold

  const computeAllRequired = (next) => {
    const requiredIds = questions.filter(q => q.required).map(q => q.id);
    if (isW3) {
      // §5 — W3 requires >= 20 chars per field to count.
      return requiredIds.every(rid => (next[rid] ?? '').toString().trim().length >= W3_THRESHOLD);
    }
    return requiredIds.every(rid => (next[rid] ?? '').toString().trim().length > 0 || rid.startsWith('g'));
  };

  useEffect(() => {
    setAllRequiredAnswered(computeAllRequired(answers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, questions]);

  // Force-flush every dirty answer to the server before submission.
  const flushDirty = useCallback(async () => {
    const dirtyIds = Object.keys(dirtyRef.current).filter(q => dirtyRef.current[q]);
    await Promise.all(dirtyIds.map(q => saveOne(q, answers[q] ?? '')));
  }, [answers, saveOne]);

  const postComplete = async (force) => {
    setCompleting(true);
    try {
      const body = force ? { force_submit: true } : {};
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/self-work/${kind}/${weekNum}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(force ? 'Draft saved and submitted.' : 'Self-Work marked complete.');
        setCompleted(true);
        setShowW3PartialConfirm(false);
      } else {
        toast.error(d.detail || 'Could not mark complete.');
      }
    } finally {
      setCompleting(false);
    }
  };

  const handleComplete = async () => {
    await flushDirty();
    // Week 3 partial-submit gate (§7): if fewer than 8 of the 12 required
    // fields meet the threshold, prompt the user with a choice rather than
    // blocking outright.  Standard rule for every other week is unchanged.
    if (isW3 && !w3MeetsHardGate) {
      setShowW3PartialConfirm(true);
      return;
    }
    await postComplete(false);
  };

  const labelOf = () => {
    if (kind === 'programme') {
      if (weekNum === 0) return 'Programme  ·  2-Week Window Self-Work';
      return `${weekLabel(weekNum, 'programme')} Self-Work`;
    }
    return `${weekLabel(weekNum, 'lesson')} Self-Work`;
  };

  // Document-level subtitle: a short, faithful, serif sentence describing
  // what this Self-Work covers.  Falls back to a neutral phrase.
  const documentSubtitle = (() => {
    if (kind === 'programme' && weekNum === 0) return 'Self-Work  ·  2-Week Window Reflection';
    if (kind === 'programme' && weekNum === CYCLE_OF_ADDICTION_INTERNAL) return 'Self-Work: The Cycle of Addiction and the Cycle of Change';
    if (kind === 'programme') return `Self-Work for ${weekLabel(weekNum, 'programme')}`;
    return `Self-Work for ${weekLabel(weekNum, 'lesson')}`;
  })();

  // Linked-week notices are currently disabled while the SuperAdmin
  // re-uploads programme content under the new W01..W30 naming convention.
  // Re-enable once we know which Programme Weeks contain the genogram /
  // family-of-origin combined Self-Work (likely Programme Weeks 4+5 and
  // 14+15, internal slots 6+7 and 16+17 -- but the user will confirm).
  const isW6or7 = false;
  const isW16or17 = false;

  return (
    <div className="min-h-screen bg-zinc-100 pb-24">
      <PageHeader title="Self-Work" />
      <main className="pt-20 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/self-work')}
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 mb-3"
            data-testid="sw-back-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back to Self-Work
          </button>

          <div className="arp-document mb-4">
            <DocumentHeader
              title="Axiom Recovery Programme (ARP)"
              subtitle={documentSubtitle}
              eyebrow={labelOf()}
              testIdPrefix="sw"
              intro={
                kind === 'programme' && weekNum >= 3 ? (
                  <span>
                    Complete this Self-Work before you move to {bareWeekLabel(weekNum + 1)}.  Use your own history honestly.  There are no right or wrong answers.  Type or use voice-to-text.  Your work auto-saves every 5&nbsp;seconds.
                  </span>
                ) : (
                  <span>
                    Type your answers or tap the microphone to dictate.  Your work auto-saves every 5&nbsp;seconds.
                  </span>
                )
              }
            />
            {isW6or7 && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 leading-relaxed flex gap-2" data-testid="sw-w6w7-note">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
                <div>
                  <strong>Programme Week 4 + Week 5 are linked.</strong>  You only need to BEGIN the Week 4 Self-Work to enter Week 5,
                  but you must COMPLETE both Week 4 AND Week 5 Self-Work before Week 6 unlocks.
                </div>
              </div>
            )}
            {isW16or17 && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 leading-relaxed flex gap-2" data-testid="sw-w16w17-note">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
                <div>
                  <strong>Programme Week 14 + Week 15 are linked.</strong>  The Self-Work for these two weeks is combined.  You only need to BEGIN the Week 14 Self-Work to enter Week 15, but you must COMPLETE both Week 14 AND Week 15 Self-Work before Week 16 unlocks.
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              {/* Week 3 progress bar: 20-char threshold across 12 required fields */}
              {isW3 && !completed && (
                <div
                  className="bg-white border border-zinc-200 rounded-xl p-4"
                  data-testid="sw-w3-progress"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                      Your progress
                    </p>
                    <p className="text-xs font-bold text-zinc-900" data-testid="sw-w3-progress-count">
                      {w3CountValid} of {w3Total} fields complete
                    </p>
                  </div>
                  <div className="w-full bg-zinc-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all ${w3MeetsHardGate ? 'bg-emerald-500' : 'bg-zinc-700'}`}
                      style={{ width: `${w3Percent}%` }}
                      data-testid="sw-w3-progress-bar"
                    />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                    A field is counted once it contains at least 20 characters.  You can submit when 8 or more of the 12 fields are complete or you can save and return later.
                  </p>
                </div>
              )}

              {questions.map((q, idx) => {
                // Week 3 introductory copy block — no input, just framing text.
                if (q.type === 'intro_w3') {
                  return (
                    <div
                      key={q.id}
                      className="bg-white border border-zinc-200 rounded-xl p-5"
                      data-testid={`sw-intro-${q.id}`}
                    >
                      <p
                        className="text-sm text-zinc-800 leading-relaxed whitespace-pre-line"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {q.prompt}
                      </p>
                    </div>
                  );
                }

                // Week 3 Cycle of Addiction reference diagram (non-interactive SVG).
                if (q.type === 'cycle_diagram_w3') {
                  return (
                    <div
                      key={q.id}
                      className="bg-white border border-zinc-200 rounded-xl p-5"
                      data-testid={`sw-diagram-${q.id}`}
                    >
                      <p
                        className="text-sm text-zinc-800 leading-relaxed mb-4"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {q.prompt}
                      </p>
                      <CycleOfAddictionDiagramW3 />
                    </div>
                  );
                }

                // Week 3 section header (no input — narrative break between
                // Part One / Part Two and the closing paragraph).
                if (q.type === 'section_header_w3') {
                  return (
                    <div
                      key={q.id}
                      className="border-t border-zinc-300 pt-6 mt-4"
                      data-testid={`sw-header-${q.id}`}
                    >
                      <p
                        className="text-sm text-zinc-800 leading-relaxed whitespace-pre-line"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {q.prompt}
                      </p>
                    </div>
                  );
                }

                // Week 3 stage panel (8 of these) — stage number + name + desc
                // + an editable longtext field that counts toward the 20-char
                // progress threshold.
                if (q.type === 'longtext_w3_stage') {
                  const val = answers[q.id] || '';
                  const len = val.trim().length;
                  const meets = len >= W3_THRESHOLD;
                  return (
                    <div
                      key={q.id}
                      className="bg-white border border-zinc-200 rounded-xl p-5"
                      data-testid={`sw-stage-${q.stage_number}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-start gap-3">
                          <span
                            className="w-7 h-7 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5"
                            data-testid={`sw-stage-${q.stage_number}-badge`}
                          >
                            {q.stage_number}
                          </span>
                          <div>
                            <p
                              className="text-base font-bold text-zinc-900"
                              style={{ fontFamily: 'Georgia, serif' }}
                            >
                              {q.stage_name}
                            </p>
                            <p className="text-xs text-zinc-600 leading-relaxed mt-1">
                              {q.stage_desc}
                            </p>
                          </div>
                        </div>
                        {saving[q.id] && <Loader2 className="w-3.5 h-3.5 text-zinc-400 animate-spin flex-shrink-0 mt-1" />}
                      </div>
                      <p className="text-sm text-zinc-800 leading-snug mt-3 mb-2 font-semibold">
                        {q.prompt}
                      </p>
                      <VoiceTextInput
                        value={val}
                        onChange={(v) => updateAnswer(q.id, v)}
                        onBlur={() => saveOnBlur(q.id)}
                        placeholder="Type or tap the mic to dictate..."
                        rows={5}
                        testId={`sw-input-${q.id}`}
                      />
                      <div className="flex items-center justify-end mt-1.5">
                        <span
                          className={`text-[11px] ${meets ? 'text-emerald-700 font-bold' : 'text-zinc-500'}`}
                          data-testid={`sw-stage-${q.stage_number}-count`}
                        >
                          {len}/{W3_THRESHOLD}+ chars{meets ? ' ✓' : ''}
                        </span>
                      </div>
                    </div>
                  );
                }

                // Week 3 reflection field (Cycle of Change — 4 of these).
                if (q.type === 'longtext_w3_reflection') {
                  const val = answers[q.id] || '';
                  const len = val.trim().length;
                  const meets = len >= W3_THRESHOLD;
                  return (
                    <div
                      key={q.id}
                      className="bg-white border border-zinc-200 rounded-xl p-5"
                      data-testid={`sw-reflection-${q.id}`}
                    >
                      <p
                        className="text-base font-bold text-zinc-900"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {q.reflection_name}
                      </p>
                      <p className="text-sm text-zinc-800 leading-snug mt-2 mb-2">
                        {q.prompt}
                      </p>
                      <VoiceTextInput
                        value={val}
                        onChange={(v) => updateAnswer(q.id, v)}
                        onBlur={() => saveOnBlur(q.id)}
                        placeholder="Type or tap the mic to dictate..."
                        rows={5}
                        testId={`sw-input-${q.id}`}
                      />
                      <div className="flex items-center justify-end mt-1.5">
                        <span
                          className={`text-[11px] ${meets ? 'text-emerald-700 font-bold' : 'text-zinc-500'}`}
                          data-testid={`sw-reflection-${q.id}-count`}
                        >
                          {len}/{W3_THRESHOLD}+ chars{meets ? ' ✓' : ''}
                        </span>
                      </div>
                    </div>
                  );
                }

                // Default rendering for every other type (existing behaviour).
                return (
                <div key={q.id} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={`sw-question-${q.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Question {idx + 1}{q.required ? '  ·  Required' : ''}</p>
                      <p className="text-sm font-semibold text-zinc-900 leading-snug mt-0.5">{q.prompt}</p>
                    </div>
                    {saving[q.id] && <Loader2 className="w-3.5 h-3.5 text-zinc-400 animate-spin flex-shrink-0 mt-1" />}
                  </div>

                  {q.type === 'genogram' ? (
                    <Genogram week={weekNum} testid={`sw-genogram-${q.id}`} />
                  ) : q.type === 'quiz' ? (
                    <div className="space-y-2" data-testid={`sw-quiz-${q.id}`}>
                      {(q.options || []).map((opt, oi) => {
                        const selected = answers[q.id] === String(oi);
                        return (
                          <button
                            key={oi}
                            onClick={() => updateAnswer(q.id, String(oi))}
                            className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                              selected ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-zinc-200 hover:border-zinc-400'
                            }`}
                            data-testid={`sw-quiz-${q.id}-opt-${oi}`}
                          >
                            <span className={`font-bold mr-2 ${selected ? 'text-white' : 'text-zinc-400'}`}>{String.fromCharCode(65 + oi)}.</span>
                            <span className="text-sm">{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : q.type === 'checklist' ? (
                    <div className="space-y-1.5" data-testid={`sw-checklist-${q.id}`}>
                      {(q.items || []).map((item, ii) => {
                        const checked = (answers[q.id] || '').split('|').includes(String(ii));
                        return (
                          <label key={ii} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-zinc-50">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const cur = (answers[q.id] || '').split('|').filter(Boolean);
                                const next = e.target.checked ? Array.from(new Set([...cur, String(ii)])) : cur.filter(x => x !== String(ii));
                                updateAnswer(q.id, next.join('|'));
                              }}
                              data-testid={`sw-checklist-${q.id}-${ii}`}
                              className="mt-0.5"
                            />
                            <span className={checked ? 'text-zinc-700' : 'text-zinc-900'}>{item}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : q.type === 'worksheet' ? (
                    <CycleOfAddictionWorksheet
                      slug={q.slug}
                      imageUrl={q.image_url}
                      value={answers[q.id] || ''}
                      onChange={(v) => updateAnswer(q.id, v)}
                      testid={`sw-worksheet-${q.id}`}
                    />
                  ) : (
                    <VoiceTextInput
                      value={answers[q.id] || ''}
                      onChange={(v) => updateAnswer(q.id, v)}
                      placeholder="Type or tap the mic to dictate..."
                      rows={q.type === 'longtext' ? 5 : 3}
                      testId={`sw-input-${q.id}`}
                    />
                  )}
                </div>
                );
              })}

              <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center justify-between gap-3" data-testid="sw-footer">
                <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                  {lastSavedAt ? (
                    <><Save className="w-3 h-3" /> Saved {lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                  ) : (
                    'Auto-saves every 5 seconds'
                  )}
                </div>
                {completed ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-bold" data-testid="sw-completed-badge">
                    <CheckCircle2 className="w-4 h-4" /> Completed
                  </span>
                ) : (
                  <Button
                    onClick={handleComplete}
                    disabled={completing || (!isW3 && !allRequiredAnswered)}
                    className="bg-zinc-950 text-white hover:bg-zinc-800 text-xs font-bold uppercase tracking-wider"
                    data-testid="sw-complete-btn"
                  >
                    {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isW3 ? 'Save and Complete' : 'Mark complete')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Week 3 partial-submit confirmation modal (§7) */}
          {showW3PartialConfirm && (
            <div
              className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
              data-testid="sw-w3-partial-modal"
            >
              <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-bold text-zinc-900" style={{ fontFamily: 'Georgia, serif' }}>
                      Submit with {w3CountValid} of {w3Total} fields complete?
                    </h3>
                    <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
                      You can save what you have now and unlock Week 4 or return to finish the remaining fields first.  Either way nothing is lost.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-5">
                  <Button
                    onClick={() => postComplete(true)}
                    disabled={completing}
                    className="bg-zinc-950 text-white hover:bg-zinc-800 text-xs font-bold uppercase tracking-wider w-full"
                    data-testid="sw-w3-submit-anyway-btn"
                  >
                    {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Submit and Unlock ${bareWeekLabel(weekNum + 1)}`}
                  </Button>
                  <Button
                    onClick={() => setShowW3PartialConfirm(false)}
                    disabled={completing}
                    variant="outline"
                    className="text-xs font-bold uppercase tracking-wider w-full"
                    data-testid="sw-w3-save-draft-btn"
                  >
                    Save Draft and Return
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
